import i18nFactory from '@yandex-int/i18n';
import { RadioButton } from 'lego-on-react';
import { compact, find, findIndex, keys, uniq } from 'lodash';
import { ChangeEvent } from 'react';
import * as React from 'react';
import { RefObject } from 'react';
import * as ReactDOM from 'react-dom';

import { Bem } from '../../service/Bem/Bem';
import { YandexMapHelper } from '../../service/YandexMapHelper/YandexMapHelper';
import { OutdoorBalloon } from '../OutdoorBalloon/OutdoorBalloon';
import { OutdoorPlacemark, placemarkColor } from '../OutdoorPlacemark/OutdoorPlacemark';
import { OutdoorPlatformList } from '../OutdoorPlatformList/OutdoorPlatformList';

import './OutdoorMap.css';
import * as keyset from './OutdoorMap.i18n';

const i18n = i18nFactory(keyset);

export interface OutdoorMapPlatform {
    type?: string;
    id: string;
    coordinates: [number, number];
    resolution: string;
    format: string;
    formatId: string;
    provider: string;
    regionId: string;
    transform?: number;
    address: string;
    caption: string;
    duration?: number;
    gid?: string;
}

export interface OutdoorMapRegionPlatform {
    platforms: OutdoorMapPlatform[];
    regionId: string;
    regionText: string;
    regionCenter: [number, number];
}

export interface OutdoorMapProps {
    selectedByRegion: {
        [id: string]: string[];
    };
    onChange: (data: {
        selectedRegion: string;
        checkedByRegion: {
            [id: string]: string[];
        };
    }) => void;
    selected?: string[];
    platformsByRegion: OutdoorMapRegionPlatform[];
    selectedRegion: string;
    renderTitle: boolean;
}
export interface OutdoorPlacemarkData {
    platforms: OutdoorMapPlatform[];
    coordinates: [number, number];
    id: string;
    selectedInToolBox: boolean;
    highlight: boolean;
    slectedIds: string[];
}

export interface OutdoorMapState {
    selected?: string[];
    selectedRegion: string;
    lastHighlightedBillboard?: null | string;
    scrollToId?: string;
    highlightId?: string;
    mapDataByRegion: {
        [id: string]: {
            billboards: OutdoorPlacemarkData[];
            regionId: string;
            regionText: string;
            regionCenter: [number, number];
        };
    };
    checkedByRegion: {
        [id: string]: string[];
    };
}

export class OutdoorMap extends React.Component<OutdoorMapProps, OutdoorMapState> {
    public static defaultProps: Partial<OutdoorMapProps> = {
        renderTitle: true
    };

    private yaMap: ymaps.Map;
    private allGeoObjects: { objectManager?: ymaps.ObjectManager } = {};
    private blockRef: RefObject<HTMLDivElement> = React.createRef();
    private regionsCenter: {} = {};
    private block = 'outdoor-map';

    constructor(props: OutdoorMapProps) {
        super(props);

        const { selected = [] } = this.props;

        this.state = {
            selected: selected,
            selectedRegion: this.props.selectedRegion,
            lastHighlightedBillboard: null,
            checkedByRegion: this.props.platformsByRegion.reduce((result, region) => {
                result[region.regionId] = this.props.selectedByRegion[region.regionId] || [];

                return result;
            }, {}),
            mapDataByRegion: {}
        };
    }

    render() {
        return (
            <div className={Bem.cls(this.block)}>
                {this.props.renderTitle && this.buildTitle()}
                {this.buildRegionNavigation()}
                <div ref={this.blockRef} className={Bem.cls(this.block, 'map')}>
                    {this.buildToolBox()}
                </div>
            </div>
        );
    }

    componentDidMount() {
        YandexMapHelper.initApiMap().then(() => {
            ymaps.ready(() => {
                this.createMap();
                this.onMapCreate();
                this.buildRegionPlacemarks();
            });
        });
    }

    private getMapDataByRegion = () => {
        return this.props.platformsByRegion.reduce((regionResult, region) => {
            const billboardsIds: string[] = [];
            const regionId = region.regionId;
            const { platformsByRegion } = this.props;
            const { bounds: uBounds } = ymaps.util;

            const regionIndex = findIndex(platformsByRegion, {
                regionId: regionId
            });
            const fromPoints = uBounds.fromPoints(
                platformsByRegion[regionIndex].platforms.map((platform: OutdoorMapPlatform) => platform.coordinates)
            );
            const regionCenter = uBounds.getCenter(fromPoints);

            const billboards = region.platforms.reduce((platforms, platform) => {
                const id = platform.coordinates.join(':');

                if (!platforms[id]) {
                    platforms[id] = {
                        id: id,
                        coordinates: platform.coordinates,
                        platforms: [],
                        highlight: false,
                        selectedInToolBox: false
                    };

                    billboardsIds.push(id);
                }

                platforms[id].platforms.push(platform);

                return platforms;
            }, {});

            regionResult[regionId] = {
                billboards: billboardsIds.map((id: string) => {
                    return billboards[id];
                }),
                regionId: regionId,
                regionText: region.regionText,
                regionCenter: regionCenter
            };

            return regionResult;
        }, {});
    };

    private buildToolBox = () => {
        const { checkedByRegion, selectedRegion } = this.state;

        const checkedItems = keys(checkedByRegion).reduce((result: string[], regionId: string) => {
            return result.concat(checkedByRegion[regionId]);
        }, []);

        return (
            <div className={Bem.cls(this.block, 'toolbox')}>
                <OutdoorPlatformList
                    checkedItems={checkedItems}
                    scrollToId={this.state.scrollToId}
                    highlightId={this.state.highlightId}
                    platformsByRegion={this.props.platformsByRegion}
                    onPlatformCheckboxChange={this.onPlatformCheckboxChange}
                    onRegionCheckBoxChange={this.onRegionCheckBoxChange}
                    onAddressClick={this.onAddressClick}
                    onFilter={this.onFilter}
                    onRegionListToggle={this.onRegionListToggle}
                    selectedRegion={selectedRegion}
                />
            </div>
        );
    };

    private onRegionListToggle = () => {
        this.setState({ scrollToId: undefined });
    };

    private onFilter = (list: (OutdoorMapRegionPlatform | OutdoorMapPlatform)[]) => {
        this.buildRegionPlacemarks(this.state.selectedRegion, list);
    };

    private onAddressClick = (platform: OutdoorMapPlatform) => {
        const { selectedRegion } = this.state;
        const billboardId = platform.coordinates.join(':');

        if (platform.regionId !== selectedRegion) {
            this.setState(
                {
                    selectedRegion: platform.regionId,
                    highlightId: platform.id,
                    scrollToId: undefined
                },
                () => {
                    this.buildRegionPlacemarks(platform.regionId);
                    this.yaMap.setCenter(platform.coordinates, 16, {
                        useMapMargin: true
                    });

                    this.highlightBillboardById(billboardId);
                }
            );
        } else {
            this.yaMap.setCenter(platform.coordinates, 16, {
                useMapMargin: true
            });

            this.highlightBillboardById(billboardId);
            this.setState({
                highlightId: platform.id,
                scrollToId: undefined
            });
        }
    };

    private highlightBillboardById = (billboardId: string) => {
        if (billboardId !== this.state.lastHighlightedBillboard) {
            if (this.state.lastHighlightedBillboard) {
                this.highlightBillboard(this.state.lastHighlightedBillboard, false);
            }

            this.highlightBillboard(billboardId, true);

            this.setState({
                lastHighlightedBillboard: billboardId
            });
        }
    };

    private onPlatformCheckboxChange = (checked: boolean, platform: OutdoorMapPlatform) => {
        const platformId = platform.id;
        this.updatePlacemark(checked, platform);

        const newCheckedByRegion = { ...this.state.checkedByRegion };

        newCheckedByRegion[platform.regionId] = checked
            ? uniq([...newCheckedByRegion[platform.regionId], platformId])
            : newCheckedByRegion[platform.regionId].filter((id: string) => id !== platformId);

        if (this.state.lastHighlightedBillboard) {
            this.highlightBillboard(this.state.lastHighlightedBillboard, false);
        }

        this.setState(
            {
                lastHighlightedBillboard: null,
                checkedByRegion: newCheckedByRegion,
                scrollToId: undefined
            },
            () => {
                const { onChange } = this.props;

                this.rebuildClusters();
                onChange({
                    selectedRegion: this.state.selectedRegion,
                    checkedByRegion: newCheckedByRegion
                });
            }
        );
    };

    private updatePlacemark = (checked: boolean, platform: OutdoorMapPlatform) => {
        const { objectManager } = this.allGeoObjects;
        const platformId = platform.id;
        const billboardId = platform.coordinates.join(':');
        const prev = objectManager.objects.getById(billboardId);

        if (prev) {
            const slectedIds = checked
                ? uniq([...prev.properties.billboard.slectedIds, platformId])
                : prev.properties.billboard.slectedIds.filter((id: string) => id !== platformId);

            objectManager.remove(prev);
            objectManager.objects.add(prev);
            objectManager.objects.setObjectProperties(billboardId, {
                ...prev.properties,
                billboard: {
                    ...prev.properties.billboard,
                    slectedIds: slectedIds
                }
            });
        }
    };

    private onRegionCheckBoxChange = (data: {
        regionId: string;
        checked: boolean;
        itemIds: string[];
        items: OutdoorMapPlatform[];
    }) => {
        const { checked, regionId, itemIds, items } = data;

        items.forEach(platform => this.updatePlacemark(checked, platform));

        this.setState(
            (state: OutdoorMapState) => {
                const newCheckedByRegion = { ...state.checkedByRegion };

                newCheckedByRegion[regionId] = checked
                    ? uniq([...newCheckedByRegion[regionId], ...itemIds])
                    : newCheckedByRegion[regionId].filter(id => !itemIds.includes(id));

                if (this.state.lastHighlightedBillboard) {
                    this.highlightBillboard(this.state.lastHighlightedBillboard, false);
                }

                return {
                    ...state,
                    checkedByRegion: newCheckedByRegion,
                    scrollToId: undefined,
                    lastHighlightedBillboard: null
                };
            },
            () => {
                const { onChange } = this.props;

                this.rebuildClusters();
                onChange({
                    selectedRegion: this.state.selectedRegion,
                    checkedByRegion: this.state.checkedByRegion
                });
            }
        );
    };

    private rebuildClusters = () => {
        this.allGeoObjects.objectManager.clusters.getAll().map((cluster: ymaps.Clusterer) => {
            const data = {};

            data[placemarkColor.green] = 0;
            data[placemarkColor.blue] = 0;

            cluster.features.forEach((placemark: ymaps.Clusterer) => {
                const billboard = placemark.properties.billboard;

                data[placemarkColor.green] = data[placemarkColor.green] + billboard.slectedIds.length;
                data[placemarkColor.blue] =
                    data[placemarkColor.blue] + (billboard.platforms.length - billboard.slectedIds.length);
            });

            cluster.properties = {
                ...cluster.properties,
                iconContent: data[placemarkColor.green] + data[placemarkColor.blue],
                data: [
                    {
                        weight: data[placemarkColor.green],
                        color: placemarkColor.green
                    },
                    {
                        weight: data[placemarkColor.blue],
                        color: placemarkColor.blue
                    }
                ]
            };

            return cluster;
        });

        this.allGeoObjects.objectManager.clusters.overlays.each((overlay: ymaps.LayerCollection) => {
            overlay.getLayout().then((icon: ymaps.LayerCollection) => {
                icon.rebuild();
            });
        });
    };

    private buildRegionNavigation = () => {
        const { platformsByRegion } = this.props;
        const { selectedRegion, checkedByRegion } = this.state;

        return (
            <div className={Bem.cls(this.block, 'region-navigation')}>
                {platformsByRegion.map((item: OutdoorMapRegionPlatform) => {
                    return (
                        <RadioButton
                            cls={Bem.cls(this.block, 'region-navigation-button')}
                            key={item.regionId}
                            size={'s'}
                            view={'classic'}
                            theme={'normal'}
                            name={'region'}
                            value={selectedRegion}
                            onChange={this.regionRadioButtonChange}
                        >
                            <RadioButton.Radio
                                controlAttrs={{
                                    title: item.regionText
                                }}
                                value={item.regionId}
                            >
                                {item.regionText + ' ' + checkedByRegion[item.regionId].length}
                            </RadioButton.Radio>
                        </RadioButton>
                    );
                })}
            </div>
        );
    };

    private buildTitle = () => {
        return (
            <div className={Bem.cls(this.block, 'title-container')}>
                <div className={Bem.cls(this.block, 'title')}>{i18n('select-ad-platforms')}</div>
                <div className={Bem.cls(this.block, 'title-description')}>{i18n('select-ad-platforms-diff-group')}</div>
            </div>
        );
    };

    private regionRadioButtonChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const newRegion = event.target.value;
        const { selectedRegion } = this.state;

        if (newRegion !== undefined) {
            if (newRegion === selectedRegion) {
                this.changeMapCenter(selectedRegion);
            } else {
                this.setState({
                    selectedRegion: newRegion
                });

                this.changeMapRegin(newRegion);

                this.props.onChange({
                    selectedRegion: newRegion,
                    checkedByRegion: this.state.checkedByRegion
                });
            }
        }
    };

    private changeMapCenter = (newRegion: string): void => {
        const region = this.regionsCenter[newRegion];

        if (region) {
            this.yaMap.setCenter(region.center, region.zoom, {
                useMapMargin: true
            });
        }
    };

    private getRegionsCenter = () => {
        const { platformsByRegion } = this.props;
        const { bounds: uBounds } = ymaps.util;
        const regionsId = platformsByRegion.map((region: OutdoorMapRegionPlatform) => region.regionId);

        regionsId.forEach((regionId: string) => {
            if (this.regionsCenter[regionId]) {
                return this.regionsCenter[regionId];
            } else {
                const regionIndex = findIndex(platformsByRegion, {
                    regionId: regionId
                });
                const fromPoints = uBounds.fromPoints(
                    platformsByRegion[regionIndex].platforms.map((platform: OutdoorMapPlatform) => platform.coordinates)
                );

                this.regionsCenter[regionId] = uBounds.getCenterAndZoom(fromPoints, this.yaMap.container.getSize());
            }
        });
    };

    private changeMapRegin = (newRegion: string): void => {
        this.buildRegionPlacemarks(newRegion);
        this.changeMapCenter(newRegion);
        this.setState({
            highlightId: undefined,
            scrollToId: undefined,
            lastHighlightedBillboard: null
        });
    };

    private buildRegionPlacemarks = (
        selectedRegion: string = this.state.selectedRegion,
        list?: (OutdoorMapRegionPlatform | OutdoorMapPlatform)[]
    ): void => {
        const checkedInRegion = this.state.checkedByRegion[selectedRegion];
        const region = this.state.mapDataByRegion[selectedRegion];
        const lastHighlightedBillboard = this.state.lastHighlightedBillboard;

        if (region && this.allGeoObjects.objectManager) {
            this.allGeoObjects.objectManager.removeAll();

            const data = {
                type: 'FeatureCollection',
                features: compact(
                    region.billboards.map(billboard => {
                        if (list && billboard.platforms.every(platform => !find(list, { id: platform.id }))) {
                            return false;
                        }

                        return {
                            type: 'Feature',
                            id: billboard.id,
                            geometry: {
                                type: 'Point',
                                coordinates: billboard.coordinates
                            },
                            options: {
                                iconImageSize: [40, 40],
                                hideIconOnBalloonOpen: false,
                                balloonOffset: [0, -14],
                                hasBalloon: billboard.platforms.length > 1,
                                openBalloonOnClick: billboard.platforms.length > 1
                            },
                            properties: {
                                billboard: {
                                    ...billboard,
                                    highlight: lastHighlightedBillboard
                                        ? billboard.id === lastHighlightedBillboard
                                        : false,
                                    slectedIds: billboard.platforms.reduce((result: string[], platform) => {
                                        if (checkedInRegion.includes(platform.id)) {
                                            result.push(platform.id);
                                        }

                                        return result;
                                    }, [])
                                }
                            }
                        };
                    })
                )
            };

            this.allGeoObjects.objectManager.add(data);
            this.rebuildClusters();
        }
    };

    private onMapCreate = (): void => {
        const customPlacemark = ymaps.templateLayoutFactory.createClass(
            '<div class="outdoor-placemark-template"</div>',
            {
                build: function() {
                    customPlacemark.superclass.build.call(this);

                    ReactDOM.render(
                        <OutdoorPlacemark billboard={this.getData().properties.billboard} />,
                        this.getParentElement().getElementsByClassName('outdoor-placemark-template')[0]
                    );
                }
            }
        );

        const onBalloonCaptionClick = this.onBalloonCaptionClick;

        const customBalloon = ymaps.templateLayoutFactory.createClass('<div class="outdoor-balloon-template"</div>', {
            build: function() {
                customPlacemark.superclass.build.call(this);
                ReactDOM.render(
                    <OutdoorBalloon
                        billboard={this.getData().properties.get('billboard')}
                        onCaptionClick={onBalloonCaptionClick}
                    />,
                    this.getParentElement().getElementsByClassName('outdoor-balloon-template')[0]
                );
            }
        });

        const objectManager = new ymaps.ObjectManager({
            clusterize: true,
            gridSize: 64,
            minClusterSize: 3,
            clusterDisableClickZoom: false
        });

        objectManager.objects.options.set({
            preset: 'islands#greenDotIcon',
            iconLayout: customPlacemark,
            balloonContentLayout: customBalloon,
            pointOverlay: 'html#placemark',
            iconPane: 'overlaps'
        });

        objectManager.clusters.options.set({
            clusterIconLayout: 'default#pieChart',
            clusterIconPieChartCoreRadius: 10,
            clusterIconPieChartStrokeWidth: 3
        });

        this.yaMap.geoObjects.add(objectManager);
        this.allGeoObjects.objectManager = objectManager;

        objectManager.objects.events.add('click', this.onPlatformClick.bind(this));

        this.yaMap.events.add('boundschange', () => {
            this.rebuildClusters();

            if (this.state.lastHighlightedBillboard) {
                this.highlightBillboard(this.state.lastHighlightedBillboard, true);
            }
        });

        this.setState({
            mapDataByRegion: this.getMapDataByRegion()
        });
    };

    private onBalloonCaptionClick = (platform: OutdoorMapPlatform) => {
        this.setState({
            scrollToId: platform.id,
            highlightId: platform.id
        });
    };

    private highlightBillboard = (id: string, highlight?: boolean) => {
        const objectManager = this.allGeoObjects.objectManager;
        const prev = objectManager.objects.getById(id);

        if (prev) {
            const newHighlightState = typeof highlight === 'boolean' ? highlight : !prev.properties.billboard.highlight;

            objectManager.objects.setObjectProperties(id, {
                ...prev.properties,
                billboard: {
                    ...prev.properties.billboard,
                    highlight: newHighlightState
                }
            });
        }
    };

    private onPlatformClick = (e: ymaps.Event) => {
        const id = e.get('objectId');
        const objectManager = this.allGeoObjects.objectManager;
        const billboard = objectManager.objects.getById(id).properties.billboard;

        if (id !== this.state.lastHighlightedBillboard) {
            if (this.state.lastHighlightedBillboard) {
                this.highlightBillboard(this.state.lastHighlightedBillboard, false);
            }

            this.highlightBillboard(id, true);

            this.setState({
                lastHighlightedBillboard: id,
                scrollToId: billboard.platforms[0].id,
                highlightId: billboard.platforms[1] ? billboard.platforms[1].id : undefined
            });
        } else {
            this.setState({
                scrollToId: billboard.platforms[0].id
            });
        }
    };

    private createMap = (): void => {
        const node = this.blockRef && this.blockRef.current;
        const currentRegionId = this.state.selectedRegion;

        if (!node) {
            throw new Error('createMap: blockRef is undefined');
        }

        this.yaMap = YandexMapHelper.createMap(
            node,
            {
                type: 'yandex#map',
                zoom: 10,
                center: [],
                controls: ['trafficControl']
            },
            {
                suppressMapOpenBlock: true,
                maxZoom: 19
            }
        );

        this.yaMap.controls.add('zoomControl', {
            position: {
                top: 100,
                left: 430
            }
        });

        this.yaMap.margin.addArea({
            left: 0,
            top: 0,
            width: 450,
            height: '100%'
        });

        // кешируем центр для каждого города
        this.getRegionsCenter();

        const centerAndZoom = this.regionsCenter[currentRegionId];

        // TODO centerAndZoom.zoom - будет слишком большим, если город один
        // TODO как вариант посчитать центр относительно всех поверхностей города
        this.yaMap.setCenter(centerAndZoom.center, centerAndZoom.zoom > 10 ? 10 : centerAndZoom.zoom, {
            useMapMargin: true
        });
    };
}
